import React, { useState, useEffect, useMemo } from 'react';
import { X, Plus, Trash2, CalendarDays } from 'lucide-react';
import {
  ProgramacionService,
  type ProgramacionSemanal,
  type CatalogosProgramacion,
  type AsignacionInput,
} from '../../services/programacion/programacion.service';
import { usePermissions } from '../../hooks/usePermissions';
import { parseFechaLocal } from '../../utils/date.utils';
import { SearchableSelect } from './SearchableSelect';

interface AsignacionRow {
  key: string;
  fecha: string;
  horaInicio: string;
  horaFin: string;
  participanteId: string;
  ministerioId: string;
  liderId: string;
  rol: string;
  actividad: string;
  observaciones: string;
  // Seguimiento (solo se conserva al editar; no se edita en el formulario)
  estado?: string;
  novedad?: string;
}

interface ProgramacionFormDrawerProps {
  isOpen: boolean;
  programacion: ProgramacionSemanal | null; // null = crear
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const DIAS_SEMANA = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

const emptyRow = (): AsignacionRow => ({
  key: crypto.randomUUID(),
  fecha: '',
  horaInicio: '',
  horaFin: '',
  participanteId: '',
  ministerioId: '',
  liderId: '',
  rol: '',
  actividad: '',
  observaciones: '',
});

const inputClass =
  'w-full px-2.5 py-2 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 outline-none bg-white';

const toDateInput = (iso: string): string => (iso ? iso.slice(0, 10) : '');

export const ProgramacionFormDrawer: React.FC<ProgramacionFormDrawerProps> = ({
  isOpen,
  programacion,
  onClose,
  onSuccess,
  onError,
}) => {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('Programación Semanal', 'editar');
  const canCreate = hasPermission('Programación Semanal', 'crear');

  const [isVisible, setIsVisible] = useState(false);
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [rows, setRows] = useState<AsignacionRow[]>([emptyRow()]);
  const [catalogos, setCatalogos] = useState<CatalogosProgramacion>({ miembros: [], ministerios: [] });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const isEditing = programacion !== null;
  const canSubmit = isEditing ? canEdit : canCreate;

  const miembroOptions = useMemo(
    () => catalogos.miembros.map((m) => ({ id: m.id, label: m.nombreCompleto })),
    [catalogos.miembros],
  );
  const ministerioOptions = useMemo(
    () => catalogos.ministerios.map((m) => ({ id: m.id, label: m.nombre })),
    [catalogos.ministerios],
  );

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    }
    setIsVisible(false);
  }, [isOpen]);

  // Cargar catálogos y datos al abrir
  useEffect(() => {
    if (!isOpen) return;
    setLoadError(null);

    ProgramacionService.getCatalogos()
      .then(setCatalogos)
      .catch(() => setLoadError('No se pudieron cargar los catálogos (miembros/ministerios)'));

    if (programacion) {
      setFechaInicio(toDateInput(programacion.fechaInicio));
      setFechaFin(toDateInput(programacion.fechaFin));
      setDescripcion(programacion.descripcion);
      // Las asignaciones vienen en el objeto solo si se cargó por ID; si no, pedirlas
      const fill = (asignaciones?: ProgramacionSemanal['asignaciones']) => {
        if (asignaciones && asignaciones.length > 0) {
          setRows(
            asignaciones.map((a) => ({
              key: crypto.randomUUID(),
              fecha: toDateInput(a.fecha),
              horaInicio: a.horaInicio,
              horaFin: a.horaFin,
              participanteId: a.participanteId ?? '',
              ministerioId: a.ministerioId ?? '',
              liderId: a.liderId ?? '',
              rol: a.rol ?? '',
              actividad: a.actividad ?? '',
              observaciones: a.observaciones ?? '',
              estado: a.estado ?? 'pendiente',
              novedad: a.novedad ?? '',
            })),
          );
        } else {
          setRows([emptyRow()]);
        }
      };
      if (programacion.asignaciones) {
        fill(programacion.asignaciones);
      } else {
        ProgramacionService.getById(programacion.id)
          .then((full) => fill(full.asignaciones))
          .catch(() => setRows([emptyRow()]));
      }
    } else {
      setFechaInicio('');
      setFechaFin('');
      setDescripcion('');
      setRows([emptyRow()]);
    }
  }, [isOpen, programacion]);

  const updateRow = (key: string, field: keyof AsignacionRow, value: string) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (key: string) =>
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : prev));

  const diaDeFecha = (fecha: string): string => {
    if (!fecha) return '';
    const d = parseFechaLocal(fecha);
    return DIAS_SEMANA[d.getDay()] ?? '';
  };

  const handleSubmit = async () => {
    if (!fechaInicio || !fechaFin || !descripcion.trim()) {
      onError('Completa la semana (fecha inicio y fin) y la descripción');
      return;
    }
    const asignaciones: AsignacionInput[] = rows
      .filter((r) => r.fecha && r.horaInicio && r.horaFin)
      .map((r) => ({
        fecha: r.fecha,
        horaInicio: r.horaInicio,
        horaFin: r.horaFin,
        participanteId: r.participanteId || undefined,
        ministerioId: r.ministerioId || undefined,
        liderId: r.liderId || undefined,
        rol: r.rol.trim() || undefined,
        actividad: r.actividad.trim() || undefined,
        observaciones: r.observaciones.trim() || undefined,
        estado: r.estado || undefined,
        novedad: r.novedad || undefined,
      }));

    setIsSubmitting(true);
    try {
      const payload = {
        fechaInicio,
        fechaFin,
        descripcion: descripcion.trim(),
        asignaciones,
      };
      if (isEditing && programacion) {
        await ProgramacionService.update(programacion.id, payload);
        onSuccess('Programación actualizada correctamente');
      } else {
        await ProgramacionService.create(payload);
        onSuccess('Programación creada correctamente');
      }
      onClose();
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Error al guardar');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed left-0 lg:left-56 right-0 bottom-0 top-0 z-[60] overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        {/* Overlay transparente (clic para cerrar) */}
        <div
          className={`absolute inset-0 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
          onClick={onClose}
        />

        {/* Panel */}
        <section
          className={`absolute right-0 top-0 h-full w-full max-w-none bg-white border-l border-gray-200 shadow-2xl transform transition-transform duration-300 ease-in-out ${
            isVisible ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center">
                  <CalendarDays className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-sm font-medium text-gray-900">
                    {isEditing ? 'Editar Programación Semanal' : 'Nueva Programación Semanal'}
                  </h2>
                  <p className="text-xs text-gray-500">
                    Define la semana, la descripción y las asignaciones del personal
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Contenido */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {loadError && (
                <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                  {loadError}
                </div>
              )}

              {/* Semana + descripción */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    Fecha inicio de la semana
                  </label>
                  <input
                    type="date"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    Fecha fin de la semana
                  </label>
                  <input
                    type="date"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div className="md:col-span-1">
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    Descripción del evento
                  </label>
                  <input
                    type="text"
                    value={descripcion}
                    onChange={(e) => setDescripcion(e.target.value)}
                    placeholder="Ej: Apoyo al diligenciamiento de la membresía"
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Asignaciones del personal (tarjetas) */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-medium text-gray-900">Asignaciones del personal</h3>
                  <button
                    onClick={addRow}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-purple-600 text-white hover:bg-purple-700 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Agregar asignación
                  </button>
                </div>

                <div className="space-y-3">
                  {rows.map((row, index) => (
                    <div key={row.key} className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                          Asignación {index + 1}
                        </span>
                        <button
                          onClick={() => removeRow(row.key)}
                          disabled={rows.length <= 1}
                          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
                          title="Eliminar asignación"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-12 gap-3">
                        {/* Fecha (con día debajo) */}
                        <div className="col-span-6 sm:col-span-4 md:col-span-2">
                          <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">Fecha</label>
                          <input
                            type="date"
                            value={row.fecha}
                            min={fechaInicio || undefined}
                            max={fechaFin || undefined}
                            onChange={(e) => updateRow(row.key, 'fecha', e.target.value)}
                            className={inputClass}
                          />
                          <p className="text-[10px] text-gray-400 mt-1">{diaDeFecha(row.fecha) || '—'}</p>
                        </div>

                        {/* Hora inicio */}
                        <div className="col-span-3 sm:col-span-4 md:col-span-2">
                          <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">H. inicio</label>
                          <input
                            type="time"
                            value={row.horaInicio}
                            onChange={(e) => updateRow(row.key, 'horaInicio', e.target.value)}
                            className={inputClass}
                          />
                        </div>

                        {/* Hora fin */}
                        <div className="col-span-3 sm:col-span-4 md:col-span-2">
                          <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">H. fin</label>
                          <input
                            type="time"
                            value={row.horaFin}
                            onChange={(e) => updateRow(row.key, 'horaFin', e.target.value)}
                            className={inputClass}
                          />
                        </div>

                        {/* Participante */}
                        <div className="col-span-12 sm:col-span-6 md:col-span-3">
                          <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">Participante</label>
                          <SearchableSelect
                            options={miembroOptions}
                            value={row.participanteId}
                            onChange={(v) => updateRow(row.key, 'participanteId', v)}
                            placeholder="Buscar participante…"
                          />
                        </div>

                        {/* Ministerio */}
                        <div className="col-span-12 sm:col-span-6 md:col-span-3">
                          <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">Ministerio</label>
                          <SearchableSelect
                            options={ministerioOptions}
                            value={row.ministerioId}
                            onChange={(v) => updateRow(row.key, 'ministerioId', v)}
                            placeholder="Buscar ministerio…"
                          />
                        </div>

                        {/* Líder a cargo */}
                        <div className="col-span-12 sm:col-span-6 md:col-span-4">
                          <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">Líder a cargo</label>
                          <SearchableSelect
                            options={miembroOptions}
                            value={row.liderId}
                            onChange={(v) => updateRow(row.key, 'liderId', v)}
                            placeholder="Buscar líder…"
                          />
                        </div>

                        {/* Rol */}
                        <div className="col-span-12 sm:col-span-6 md:col-span-2">
                          <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">Rol</label>
                          <input
                            type="text"
                            value={row.rol}
                            onChange={(e) => updateRow(row.key, 'rol', e.target.value)}
                            placeholder="Ej: Recepción"
                            className={inputClass}
                          />
                        </div>

                        {/* Actividad */}
                        <div className="col-span-12 sm:col-span-6 md:col-span-3">
                          <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">Actividad</label>
                          <input
                            type="text"
                            value={row.actividad}
                            onChange={(e) => updateRow(row.key, 'actividad', e.target.value)}
                            placeholder="Ej: Apoyo en entrada"
                            className={inputClass}
                          />
                        </div>

                        {/* Observaciones */}
                        <div className="col-span-12 sm:col-span-6 md:col-span-3">
                          <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">Observaciones</label>
                          <input
                            type="text"
                            value={row.observaciones}
                            onChange={(e) => updateRow(row.key, 'observaciones', e.target.value)}
                            className={inputClass}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400 mt-2">
                  Las asignaciones sin fecha u horas se ignoran al guardar.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 shrink-0">
              <button
                onClick={onClose}
                className="px-4 py-2.5 rounded-lg text-xs font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !canSubmit}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs font-medium bg-purple-600 text-white hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Guardando…' : isEditing ? 'Guardar cambios' : 'Crear programación'}
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
